// src/services/financeVaultStorage.ts
// ============================================
// Phase 2.3 财务数据 Vault 化：把分散在 localStorage 的财务数据（支出明细/报表）
// 写透到 Vault（XEAOS/Finance/），实现跨设备唯一真相源 + 接口级鉴权可依。
//
// 兼容策略（不破坏既有组件）：
//   - 读：优先 Vault，Vault 无/失败时回落 localStorage（保留存量数据迁移）
//   - 写：写透 Vault + localStorage 双写（本地兜底，Vault 起不依赖）
//   - hydrate：App/组件挂载时把 Vault 数据同步进 localStorage key，供同步组件使用
// ============================================

const FINANCE_DIR = "XEAOS/Finance";

/**
 * 从 Vault 读取财务 JSON 文件；Vault 无数据/失败时回退到 localStorage key。
 */
export async function loadFinanceJson<T>(
  fileName: string,
  fallbackKey: string,
  fallback: T
): Promise<T> {
  try {
    const resp = await fetch(
      `/vault-api/read?path=${encodeURIComponent(`${FINANCE_DIR}/${fileName}`)}`
    ).catch(() => null);
    if (resp && resp.ok) {
      const r = await resp.json();
      if (r.success && r.content) {
        const parsed = JSON.parse(r.content);
        return parsed as T;
      }
    }
  } catch {
    /* 忽略读取异常 */
  }
  // 回落：localStorage
  try {
    const raw = localStorage.getItem(fallbackKey);
    if (raw) return JSON.parse(raw) as T;
  } catch {
    /* ignore */
  }
  return fallback;
}

/**
 * 写透：写 Vault JSON + localStorage key（不抛异常，失败仅警告），供保存方无感调用。
 */
export async function writeFinanceJson<T>(
  fileName: string,
  fallbackKey: string,
  data: T
): Promise<void> {
  const content = JSON.stringify(data, null, 2);
  try {
    await fetch(
      `/vault-api/write?path=${encodeURIComponent(`${FINANCE_DIR}/${fileName}`)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content, skipMeta: true }),
      }
    ).catch(() => null);
  } catch {
    /* ignore */
  }
  try {
    localStorage.setItem(fallbackKey, content);
  } catch {
    /* ignore */
  }
}

/**
 * hydrate：把 Vault 里已有的财务数据同步进 localStorage key（供同步读的组件使用）。
 * 返回是否成功（Vault 有数据且已回填），组件可据此 setState 刷新。
 */
export async function hydrateFinanceKey<T>(
  fileName: string,
  fallbackKey: string
): Promise<T | null> {
  try {
    const value = await loadFinanceJson<T | null>(fileName, fallbackKey, null);
    if (value == null) return null;
    localStorage.setItem(fallbackKey, JSON.stringify(value));
    return value;
  } catch {
    return null;
  }
}